import type { DossierFile, DossierFileSource } from "../types";

/**
 * Demo mode: a contact's files live in memory for the visit. Every contact
 * opens on the same two files, so the module is never shown empty; what the
 * visitor drops is added on top and opens for real (its blob is kept).
 */
const DAY = 24 * 3600 * 1000;

const files = new Map<string, DossierFile[]>();
const blobs = new Map<string, Blob>();

const seed = (customerId: string): DossierFile[] => {
  const base = {
    client_id: "demo",
    customer_id: customerId,
    created_by: "demo_user",
  };
  return [
    {
      ...base,
      id: `demo_file_${customerId}_quote`,
      filename: "Devis-signé.pdf",
      content_type: "application/pdf",
      size: 184_320,
      uploaded_by_name: "Camille Martin",
      origin: "message",
      origin_id: "",
      created_at: Date.now() - 2 * DAY,
    },
    {
      ...base,
      id: `demo_file_${customerId}_photo`,
      filename: "Photo-du-dégât.jpg",
      content_type: "image/jpeg",
      size: 1_245_184,
      uploaded_by_name: "Camille Martin",
      origin: "contact",
      origin_id: "",
      created_at: Date.now() - 6 * DAY,
    },
  ];
};

const filesOf = (customerId: string) => {
  if (!files.has(customerId)) files.set(customerId, seed(customerId));
  return files.get(customerId) as DossierFile[];
};

export const listMockFiles = (customerId: string) => ({
  files: [...filesOf(customerId)],
});

export const uploadMockFile = (
  customerId: string,
  file: File,
  source: DossierFileSource,
) => {
  const saved: DossierFile = {
    id: `demo_file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    client_id: "demo",
    customer_id: customerId,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
    uploaded_by_name: "Vous",
    origin: source.origin,
    origin_id: source.originId || "",
    created_at: Date.now(),
    created_by: "demo_user",
  };
  blobs.set(saved.id, file);
  files.set(customerId, [saved, ...filesOf(customerId)]);
  return { file: saved };
};

export const downloadMockFile = (fileId: string): Blob =>
  blobs.get(fileId) ||
  new Blob(["Fichier de démonstration."], { type: "text/plain" });

export const removeMockFile = (customerId: string, fileId: string) => {
  files.set(
    customerId,
    filesOf(customerId).filter((file) => file.id !== fileId),
  );
  blobs.delete(fileId);
  return { success: true };
};

export const renameMockFile = (
  customerId: string,
  fileId: string,
  filename: string,
) => {
  const renamed = filesOf(customerId).find((file) => file.id === fileId);
  if (!renamed) throw new Error("File not found");
  renamed.filename = filename;
  return { file: renamed };
};
